<template>
  <Modal
    :show="show"
    title="Seleccionar Productos Similares"
    @close="emit('close')"
  >
    <div class="space-y-6">
      <!-- Buscador -->
      <div class="relative">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <font-awesome-icon icon="search" class="text-slate-400" />
        </div>
        <input
          v-model="searchTerm"
          type="text"
          class="input-field pl-10"
          placeholder="Buscar productos..."
        />
      </div>

      <!-- Lista de productos -->
      <div class="border rounded-lg overflow-hidden">
        <div class="max-h-96 overflow-y-auto">
          <table class="table-elegant w-full">
            <thead class="sticky top-0 bg-white">
              <tr>
                <th class="w-8"></th>
                <th>Nombre</th>
                <th>Laboratorio</th>
                <th>Precio</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="product in filteredProducts" :key="product.id">
                <td class="text-center">
                  <input
                    type="checkbox"
                    :checked="isSelected(product)"
                    @change="toggleProduct(product)"
                    class="rounded text-indigo-600 focus:ring-indigo-500"
                  />
                </td>
                <td class="font-medium">{{ product.Nombre }}</td>
                <td>{{ product.laboratories[0]?.laboratory.Nombre }}</td>
                <td class="text-indigo-600 font-medium">
                  ${{ calculatePrice(product).toLocaleString() }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Botones de acción -->
      <div class="flex justify-end space-x-4">
        <button
          @click="emit('close')"
          class="px-4 py-2 text-slate-600 hover:text-slate-800"
        >
          Cancelar
        </button>
        <button
          @click="handleSave"
          class="btn-primary"
        >
          Guardar Selección ({{ selectedProducts.length }})
        </button>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import type { ProductWithDetails } from '../../types';
import Modal from '../Modal.vue';

const props = defineProps<{
  show: boolean;
  currentProduct: ProductWithDetails;
  availableProducts: ProductWithDetails[];
  initialSelection?: string[];
}>();

const emit = defineEmits<{
  'close': [];
  'save': [string[]];
}>();

const searchTerm = ref('');
const selectedProducts = ref<ProductWithDetails[]>([]);

// Inicializar selección
if (props.initialSelection) {
  selectedProducts.value = props.availableProducts.filter(
    p => props.initialSelection?.includes(p.id!)
  );
}

// Productos filtrados
const filteredProducts = computed(() => {
  return props.availableProducts
    .filter(product => 
      // Excluir el producto actual
      product.id !== props.currentProduct.id &&
      // Filtrar por término de búsqueda
      product.Nombre.toLowerCase().includes(searchTerm.value.toLowerCase()) &&
      // Solo mostrar productos de la misma categoría
      product.Categoria === props.currentProduct.Categoria
    );
});

// Verificar si un producto está seleccionado
const isSelected = (product: ProductWithDetails) => {
  return selectedProducts.value.some(p => p.id === product.id);
};

// Alternar selección de producto
const toggleProduct = (product: ProductWithDetails) => {
  if (isSelected(product)) {
    selectedProducts.value = selectedProducts.value.filter(p => p.id !== product.id);
  } else {
    selectedProducts.value.push(product);
  }
};

// Calcular precio de venta
const calculatePrice = (product: ProductWithDetails) => {
  const lab = product.laboratories[0];
  if (!lab) return 0;
  return Math.round(lab.details.PrecioDeCosto * product.Factor);
};

// Guardar selección
const handleSave = () => {
  emit('save', selectedProducts.value.map(p => p.id!));
};
</script>